import React, { useId, useMemo } from "react";

const CHART = {
  width: 720,
  height: 220,
  padding: { top: 18, right: 56, bottom: 40, left: 46 },
};

function numericScore(test) {
  const raw =
    test.testResultNumber != null && test.testResultNumber !== ""
      ? test.testResultNumber
      : test.result;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function formatScore(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatAxisDate(date) {
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function niceMax(value) {
  if (value <= 0) return 1;
  const padded = value * 1.15;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  const normalized = padded / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function buildSeries(tests) {
  return tests
    .map((test) => {
      const score = numericScore(test);
      const time = new Date(test.testDay).getTime();
      if (score == null || Number.isNaN(time)) return null;
      return { score, date: new Date(time) };
    })
    .filter(Boolean)
    .sort((left, right) => left.date - right.date);
}

export default function TestScoreTrendChart({ testName, tests }) {
  const gradientId = `score-trend-${useId().replace(/:/g, "")}`;
  const series = useMemo(() => buildSeries(tests), [tests]);
  const yMax = useMemo(
    () => niceMax(Math.max(...series.map((point) => point.score), 0)),
    [series]
  );

  if (series.length === 0) {
    return (
      <p className="expedientes-score-chart-empty">
        No hay puntuaciones numéricas para graficar esta prueba.
      </p>
    );
  }

  const innerWidth = CHART.width - CHART.padding.left - CHART.padding.right;
  const innerHeight = CHART.height - CHART.padding.top - CHART.padding.bottom;
  const baseline = CHART.padding.top + innerHeight;

  const coords = series.map((point, index) => {
    const x = series.length === 1
      ? CHART.padding.left + innerWidth / 2
      : CHART.padding.left + (innerWidth * index) / (series.length - 1);
    const y = baseline - (point.score / yMax) * innerHeight;
    return { ...point, x, y };
  });

  const linePath = coords
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`)
    .join(" ");
  const areaPath = `${linePath} L${coords[coords.length - 1].x},${baseline} L${coords[0].x},${baseline} Z`;

  const yTicks = [0, yMax / 2, yMax];
  const labelStep = coords.length > 6 ? Math.ceil(coords.length / 6) : 1;
  const first = coords[0];
  const last = coords[coords.length - 1];
  const delta = last.score - first.score;
  const deltaLabel = `${delta > 0 ? "+" : ""}${formatScore(delta)}`;

  return (
    <section className="expedientes-score-chart">
      <header className="expedientes-score-chart-header">
        <h4 className="expedientes-score-chart-title">Evolución de la puntuación</h4>
        {coords.length > 1 && (
          <p className="expedientes-score-chart-caption">
            {formatScore(first.score)} pts ({formatAxisDate(first.date)})
            {" → "}
            {formatScore(last.score)} pts ({formatAxisDate(last.date)})
            {" · "}
            <span className={delta >= 0 ? "expedientes-score-chart-delta--up" : "expedientes-score-chart-delta--down"}>
              {deltaLabel}
            </span>
          </p>
        )}
      </header>
      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        className="expedientes-score-chart-svg"
        role="img"
        aria-label={`Evolución de ${testName}: ${coords
          .map((point) => `${formatScore(point.score)} el ${formatAxisDate(point.date)}`)
          .join(", ")}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0063c7" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#0063c7" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => {
          const y = baseline - (tick / yMax) * innerHeight;
          return (
            <g key={tick}>
              <line
                x1={CHART.padding.left}
                y1={y}
                x2={CHART.width - CHART.padding.right}
                y2={y}
                className="expedientes-score-chart-grid"
              />
              <text
                x={CHART.padding.left - 10}
                y={y + 4}
                className="expedientes-score-chart-label"
                textAnchor="end"
              >
                {formatScore(tick)}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path d={linePath} className="expedientes-score-chart-line" />

        {coords.map((point, index) => {
          const showLabel = index % labelStep === 0 || index === coords.length - 1;
          return (
            <g key={`${point.date.toISOString()}-${index}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5.5"
                className="expedientes-score-chart-point"
              />
              <title>{`${formatAxisDate(point.date)}: ${formatScore(point.score)} pts`}</title>
              {showLabel && (
                <text
                  x={point.x}
                  y={CHART.height - 12}
                  className="expedientes-score-chart-label"
                  textAnchor="middle"
                >
                  {formatAxisDate(point.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </section>
  );
}
